import type {
  ChatMessage,
  MatchResultEntry,
  MatchStateSnapshot,
  PresenceMember,
  SeatRequest,
  SnapshotPlayer,
  WsError,
} from './payloads.js';
import type { MatchStatus } from '../games.js';

/** Events socle (spec 02 §8). Format `scope:action`. Source unique front+back.
 *  (Les namespaces de jeu sont définis dans games/<jeu>/events.ts — ADR 0005.) */
export const MatchEvents = {
  Join: 'match:join',
  Leave: 'match:leave',
  State: 'match:state',
  Status: 'match:status',
  PlayerJoined: 'match:player_joined',
  PlayerLeft: 'match:player_left',
  Ended: 'match:ended',
  // Gestion hôte/spectateurs (ADR 0007)
  RequestSeat: 'match:request_seat', // viewer → serveur : demande à jouer
  ApproveSeat: 'match:approve_seat', // host → serveur : admet un demandeur
  SeatRequests: 'match:seat_requests', // serveur → host : liste des demandes
} as const;

export const ChatEvents = { Send: 'chat:send', Message: 'chat:message' } as const;
export const PresenceEvents = { Typing: 'presence:typing', Update: 'presence:update' } as const;
export const ErrorEvent = 'error' as const;

export interface JoinPayload {
  matchId: string;
  joinGrant?: string;
}

/** Map d'events serveur → client (partagée back + front, étendue par jeu). */
export interface ServerToClient {
  'match:state': (snapshot: MatchStateSnapshot) => void;
  'match:status': (p: { status: MatchStatus }) => void;
  'match:player_joined': (p: { player: SnapshotPlayer }) => void;
  'match:player_left': (p: { userId: string }) => void;
  'match:ended': (p: {
    status: 'finished' | 'aborted';
    winnerUserId?: string;
    results: MatchResultEntry[];
    reason?: string;
  }) => void;
  'presence:update': (p: { members: PresenceMember[] }) => void;
  'match:seat_requests': (p: { requests: SeatRequest[] }) => void;
  'chat:message': (m: ChatMessage) => void;
  error: (e: WsError) => void;
}

/** Map d'events client → serveur (partagée back + front, étendue par jeu). */
export interface ClientToServer {
  'match:join': (p: JoinPayload, ack: (r: MatchStateSnapshot | WsError) => void) => void;
  'match:leave': (p: { matchId: string }) => void;
  'match:request_seat': (p: { matchId: string }) => void;
  'match:approve_seat': (p: { matchId: string; userId: string }) => void;
  'chat:send': (p: { matchId: string; body: string }, ack: (r: { id: string } | WsError) => void) => void;
  'presence:typing': (p: { matchId: string; typing: boolean }) => void;
}
